import React, { useState } from 'react'
import { XMarkIcon, Square2StackIcon, CheckIcon } from '@heroicons/react/24/outline'
import { useStaff } from '@/contexts/StaffContext'
import { toast } from 'react-hot-toast'
import { usePopup } from '@/contexts/PopupContext'

interface InviteLinkPopupProps {
  isOpen: boolean
  onClose: () => void
}

interface InviteLink {
  id: string
  title: string
  prefix: string
}

const INVITE_LINKS: InviteLink[] = [
  {
    id: 'minigame',
    title: 'Mini game nhận quà',
    prefix: 'mng'
  },
  {
    id: 'member',
    title: 'Đăng ký thành viên',
    prefix: 'mbs'
  },
  {
    id: 'group',
    title: 'Tham gia nhóm',
    prefix: 'group'
  },
  {
    id: 'order',
    title: 'Đặt mua sản phẩm',
    prefix: 'dh'
  },
  {
    id: 'website',
    title: 'Website',
    prefix: 'ws'
  }
]

export const InviteLinkPopup: React.FC<InviteLinkPopupProps> = ({ isOpen, onClose }) => {
  const { staffInfo } = useStaff()
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null)
  const { openPopup, closePopup } = usePopup()
  const prevIsOpen = React.useRef(isOpen)

  React.useEffect(() => {
    if (!prevIsOpen.current && isOpen) {
      openPopup()
    }
    if (prevIsOpen.current && !isOpen) {
      closePopup()
    }
    prevIsOpen.current = isOpen
  }, [isOpen])

  if (!isOpen) return null

  // Giả lập ID nhân viên
  const staffCode = '187281AGC1881'

  const handleCopyLink = (link: InviteLink) => {
    const fullLink = `https://pconnext.io/${link.prefix}/${staffCode}`
    navigator.clipboard.writeText(fullLink)
    setCopiedLinkId(link.id)
    toast.success('Đã sao chép!')
    
    // Reset trạng thái copied sau 2 giây
    setTimeout(() => {
      setCopiedLinkId(null)
    }, 2000)
  }

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 w-full h-full bg-black/50 z-50 flex items-center justify-center"
        onClick={onClose}
      />
      
      {/* Popup */}
      <div className="fixed p-2 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:max-w-[420px] max-w-[90%] max-h-[80vh] bg-white rounded-2xl shadow-2xl z-50">
        {/* Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="pr-8">
            <h2 className="text-lg font-semibold text-gray-900">
              Link mời của bạn
            </h2>
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
              Nếu khách hàng bấm vào link của bạn và mua hàng sau đó, doanh số sẽ được tính cho bạn!
            </p>
          </div>
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-2 rounded-xl hover:bg-gray-100 active:bg-gray-200 transition-colors duration-200"
          >
            <XMarkIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto py-6 px-8">
          <div className="flex flex-col divide-y divide-gray-100">
            {INVITE_LINKS.map(link => (
              <div 
                key={link.id}
                className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-gray-900">
                      {link.title}
                    </span>
                  </div>
                  <div className="mt-0.5 text-sm text-blue-600 font-mono truncate">
                    pconnext.io/{link.prefix}/{staffCode}
                  </div>
                </div>
                <button
                  onClick={() => handleCopyLink(link)}
                  className={`
                    ml-3 p-2 rounded-lg flex-shrink-0
                    ${copiedLinkId === link.id 
                      ? 'bg-blue-50 text-blue-600' 
                      : 'text-gray-400 hover:text-gray-500 hover:bg-gray-100 active:bg-gray-200'
                    }
                    transition-all duration-200
                  `}
                >
                  {copiedLinkId === link.id 
                    ? <CheckIcon className="w-5 h-5" />
                    : <Square2StackIcon className="w-5 h-5" />
                  }
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100">
          <div className="text-xs text-gray-400 text-center">
            Link này sẽ được ghi nhận cho {staffInfo.fullName || staffInfo.nickname} - {staffInfo.store}
          </div>
        </div>
      </div>
    </>
  )
} 